<template>
    <div class="d-flex flex-column">
        <form ref="auditForm" @submit.prevent="postAudit" class="schedule-form">
            <Field
                v-if="auditTemplates.length"
                label="Audit Template"
                v-model="localAudit.template_id"
                type="select"
                :required="true"
                :params="{
                    options: auditTemplates,
                    searchable: true,
                    allowEmpty: false,
                }"
            />

            <!-- Auditor Selection (Alpha feature) -->
            <ConditionalField
                v-if="$userStore.hasAlphaAccess"
                label="Auditor Selection"
                v-model="auditorSelectionMode"
                :options="[
                    { value: 'manual', label: 'Select Auditor' },
                    { value: 'role', label: 'Select by Role' },
                ]"
                name="auditorSelection"
                @change="handleAuditorSelectionModeChange"
            >
                <!-- Manual Selection Dropdown -->
                <template #manual>
                    <Field
                        label="Auditor Name"
                        v-model="localAudit.auditor_id"
                        type="select"
                        :required="true"
                        :params="{
                            options: userOptions,
                            placeholder: 'Select an auditor',
                            searchable: true,
                            allowEmpty: false,
                        }"
                    />
                </template>

                <!-- Select by Role Mode -->
                <template #role>
                    <RoleDropdownField
                        label="Role"
                        v-model="selectedRoleFilter"
                        :required="true"
                        placeholder="Select a role to filter auditors"
                    />

                    <!-- Show dropdown when role is selected -->
                    <Field
                        v-if="selectedRoleFilter"
                        label="Auditor Name(s)"
                        v-model="localAudit.auditor_ids"
                        type="multi-select"
                        :required="true"
                        :params="{
                            options: filteredUserOptions,
                            placeholder: 'Select auditors',
                            searchable: true,
                            allowEmpty: false,
                        }"
                        class="mt-3"
                    />
                </template>
            </ConditionalField>

            <!-- Non-alpha fallback: Basic auditor selection -->
            <Field
                v-else
                label="Auditor Name"
                v-model="localAudit.auditor_id"
                type="select"
                :required="true"
                :params="{
                    options: userOptions,
                    placeholder: 'Select an auditor',
                    searchable: true,
                    allowEmpty: false,
                }"
            />

            <!-- Site -->
            <div v-if="localAudit?.template_id">
                <Field
                    label="Site"
                    v-model="localAudit.site_id"
                    :required="true"
                    :disabled="siteOptions.length <= 1"
                    type="select"
                    :params="{
                        options: siteOptions,
                        placeholder:
                            siteOptions.length > 0
                                ? 'Select a site'
                                : 'No sites available',
                        searchable: true,
                        allowEmpty: false,
                    }"
                />

                <span class="text-muted" style="font-size: 10pt">
                    Only users assigned to this site may view this audit
                </span>
            </div>

            <!-- Area -->
            <Field
                v-if="localAudit?.template_id"
                label="Area"
                v-model="localAudit.area_id"
                :required="true"
                :disabled="areaOptions.length == 1"
                type="select"
                :params="{
                    options: areaOptions,
                    placeholder: 'Select an area',
                    searchable: true,
                    allowEmpty: true,
                }"
                style="width: 100%"
            />

            <!-- Date -->
            <Field
                label="Date"
                v-model="localAudit.date"
                type="date"
                :required="true"
                :params="{
                    minDate: minDate,
                }"
            />

            <!-- Repeat Dates -->
            <Field
                v-if="prodReady"
                label="Schedule recurring audit"
                v-model="scheduledEvent.is_renewable"
                type="checkbox"
            />

            <!-- Frequency Section -->
            <div
                v-if="scheduledEvent.is_renewable && prodReady"
                class="schedule-form"
            >
                <h3>Frequency</h3>
                <div class="frequency-group">
                    <label>Every</label>
                    <Field
                        v-model="scheduledEvent.renewal_value"
                        class="flex-fill"
                        type="select"
                        :required="true"
                        :params="{
                            options: frequencyValues,
                            searchable: true,
                            allowEmpty: false,
                        }"
                    />
                    <Field
                        v-model="scheduledEvent.renewal_unit"
                        class="flex-fill"
                        type="select"
                        :required="true"
                        :params="{
                            options: frequencyUnits,
                            searchable: true,
                            allowEmpty: false,
                        }"
                    />
                </div>
            </div>

            <!-- Submit -->
            <div class="pt-4 border-top mt-4">
                <button type="submit" class="btn btn-secondary w-100">
                    Schedule Now
                </button>
            </div>
        </form>
    </div>
</template>

<script>
import moment from 'moment';
import Field from '../Field.vue';
import RoleDropdownField from '@/components/fields/RoleDropdownField.vue';
import ConditionalField from '@/components/fields/ConditionalField.vue';
import { useAuditStore } from '@/stores/audits';
import { useSiteStore } from '@/stores/site';
import { useAreaStore } from '@/stores/area';

export default {
    name: 'ScheduleAuditForm',
    components: {
        Field,
        RoleDropdownField,
        ConditionalField,
    },
    props: {
        audit: {
            type: Object,
            default: () => ({}),
        },
        userOptions: {
            type: Array,
            required: true,
        },
    },
    data() {
        return {
            localAudit: {},
            auditTemplates: [],
            auditAreas: [],
            areaStore: null,
            auditSites: [],
            siteStore: null,
            auditorSelectionMode: 'manual',
            selectedRoleFilter: null,
            scheduledEvent: {
                audit_id: null,
                type: 'AUDIT',
                is_renewable: false,
                start_datetime: Date.now(),
            },
            frequencyValues: [
                { label: '1', value: '1' },
                { label: '2', value: '2' },
                { label: '3', value: '3' },
                { label: '4', value: '4' },
                { label: '5', value: '5' },
                { label: '6', value: '6' },
                { label: '7', value: '7' },
                { label: '8', value: '8' },
                { label: '9', value: '9' },
                { label: '10', value: '10' },
                { label: '11', value: '11' },
                { label: '12', value: '12' },
            ],
            frequencyUnits: [
                { label: 'day(s)', value: 'DAY' },
                { label: 'week(s)', value: 'WEEK' },
                { label: 'month(s)', value: 'MONTH' },
                { label: 'year(s)', value: 'YEAR' },
            ],
            prodReady: false,
        };
    },
    computed: {
        minDate() {
            return moment().startOf('day').toDate();
        },
        filteredUserOptions() {
            // If in role mode and a role is selected, filter users by that role
            if (
                this.auditorSelectionMode === 'role' &&
                this.selectedRoleFilter
            ) {
                return this.$employeesStore.getEmployeeOptionsByRole(
                    this.selectedRoleFilter,
                    'string'
                );
            }
            return this.userOptions;
        },
        areaOptions() {
            if (this.auditAreas?.length > 0) {
                return this.auditAreas;
            }

            if (this.areaStore) {
                return (this.areaStore?.getAreaOptions || []).map((opt) => ({
                    label: opt.label ?? opt.text,
                    value: opt.value,
                }));
            }
            return [];
        },
        siteOptions() {
            const storeOptions = this.siteStore?.getSiteOptions || [];
            const auditIds = new Set(
                this.auditSites?.map((s) => Number(s.value)) || []
            );
            const userIds = new Set(
                this.$userStore?.user?.sites?.map((s) => s.id) || []
            );

            return storeOptions.filter((opt) => {
                const id = Number(opt.value);

                // must be in auditSites if any are defined
                const auditOk = auditIds.size === 0 || auditIds.has(id);

                // must be in userSites if not admin and any are defined
                const userOk =
                    this.isAdminRoute || userIds.size === 0 || userIds.has(id);

                return auditOk && userOk;
            });
        },
    },
    methods: {
        loadAuditTemplates() {
            const params = {
                fields: ['id', 'name'],
                order_by: { name: 'asc' },
            };
            this.$getData('audit/template/read', params).then((response) => {
                if (response?.success) {
                    const templates = response.data || [];
                    this.auditTemplates = templates.map((template) => ({
                        label: template.name,
                        value: String(template.id),
                    }));
                } else {
                    this.$alertStore.pushError(response?.message);
                }
            });
        },
        loadAuditTemplateOptions() {
            const params = {
                where: { id: Number(this.localAudit.template_id) },
                fields: ['id'],
                include: {
                    sites: {
                        fields: ['id', 'name'],
                    },
                    areas: {
                        fields: ['id', 'name'],
                    },
                },
            };
            this.$getData('audit/template/read', params).then((response) => {
                if (response?.success && response.data) {
                    const { id, sites, areas } = response.data?.[0];

                    const siteOptions =
                        sites?.map((s) => ({
                            label: s.name,
                            value: String(s.id),
                        })) || [];

                    const areaOptions =
                        areas?.map((a) => ({
                            label: a.name,
                            value: String(a.id),
                        })) || [];

                    this.auditSites = siteOptions;
                    this.auditAreas = areaOptions;
                } else {
                    this.$alertStore.pushError(
                        response?.message || 'Failed to load template'
                    );
                }
            });
        },
        async postAudit() {
            // Validate that the date is not in the past
            if (this.localAudit.date) {
                const selectedDate = moment(this.localAudit.date).startOf(
                    'day'
                );
                const today = moment().startOf('day');

                if (selectedDate.isBefore(today)) {
                    this.$alertStore.pushError(
                        'Cannot schedule audits with dates in the past. Please select today or a future date.'
                    );
                    return;
                }
            }

            const selectedType = this.auditTemplates.find(
                (type) => type.value === this.localAudit.template_id
            );

            // Determine which auditors to use
            const auditorIds =
                this.auditorSelectionMode === 'role' &&
                this.localAudit.auditor_ids?.length > 0
                    ? this.localAudit.auditor_ids
                    : this.localAudit.auditor_id
                    ? [this.localAudit.auditor_id]
                    : [];

            if (auditorIds.length === 0) {
                this.$alertStore.pushError(
                    'Please select at least one auditor'
                );
                return;
            }

            const endpoint = this.localAudit?.id
                ? 'audit/update'
                : 'audit/create';

            // Create all audits in parallel using Promise.all
            // Works efficiently for both single auditor and multiple auditors
            const auditPromises = auditorIds.map((auditorId) => {
                const new_audit = {
                    ...this.localAudit,
                    name: selectedType?.label || this.localAudit.name || 'N/A',
                    auditor_id: auditorId,
                };

                // Remove auditor_ids from the payload (backend doesn't expect it)
                delete new_audit.auditor_ids;

                return this.$postData(endpoint, new_audit);
            });

            // Execute all audit creation requests in parallel
            const results = await Promise.all(auditPromises);

            // Count successes and failures
            let successCount = 0;
            let failCount = 0;

            results.forEach((response, index) => {
                if (response?.success) {
                    successCount++;
                } else {
                    failCount++;
                    const auditorId = auditorIds[index];
                    const auditor = this.userOptions.find(
                        (u) => u.value === auditorId
                    );
                    const auditorName = auditor?.text || 'Unknown auditor';
                    this.$alertStore.pushError(
                        `Failed to create audit for ${auditorName}: ${
                            response?.message || 'Unknown error'
                        }`
                    );
                }
            });

            if (successCount > 0) {
                const auditStore = useAuditStore();
                auditStore.incrementAuditScheduleVersionKey();

                if (auditorIds.length > 1) {
                    this.$alertStore.pushSuccess(
                        `Successfully scheduled ${successCount} audit${
                            successCount !== 1 ? 's' : ''
                        }`
                    );
                }

                this.$emit('submit');
            }

            if (failCount > 0 && successCount === 0) {
                this.$alertStore.pushError('Failed to schedule any audits');
            }
        },
        initializeAudit() {
            this.localAudit = Object.fromEntries(
                Object.entries(this.audit).filter(
                    ([_, value]) => value !== null
                )
            );
            this.localAudit.template_id = this.audit.template_id;
        },
        handleAuditorSelectionModeChange(newMode) {
            // Clear auditor and role selection when switching modes
            this.localAudit.auditor_id = null;
            this.localAudit.auditor_ids = [];
            this.selectedRoleFilter = null;
        },
    },
    watch: {
        siteOptions(newVal) {
            if (newVal && Array.isArray(newVal) && newVal.length === 1) {
                this.localAudit.site_id = newVal[0]?.value;
            }
        },
        areaOptions(newVal) {
            if (newVal && Array.isArray(newVal) && newVal.length === 1) {
                this.localAudit.area_id = newVal[0]?.value;
            }
        },
        'localAudit.template_id'(newVal, oldVal) {
            if (newVal && oldVal !== undefined && newVal !== oldVal) {
                this.localAudit.area_id = null;
                this.localAudit.site_id = null;
            }
            if (newVal) {
                this.loadAuditTemplateOptions();
            }
        },
        selectedRoleFilter(roleId) {
            // Clear auditor selection when role changes
            this.localAudit.auditor_ids = [];

            if (!roleId) return;

            // Auto-select all auditors with the selected role
            this.localAudit.auditor_ids = this.$employeesStore
                .getEmployeesByRole(roleId)
                .map((id) => String(id));
        },
    },
    mounted() {
        this.initializeAudit();
    },
    created() {
        this.siteStore = useSiteStore();
        this.siteStore.syncSites();
        this.areaStore = useAreaStore();
        this.areaStore.syncAreas();
        this.loadAuditTemplates();
    },
};
</script>

<style scoped>
.schedule-form-container {
    padding: 1.5rem;
    background-color: #f9f9f9;
    border-radius: 1rem;
    max-width: 400px;
    margin: auto;
}

.schedule-form {
    display: flex;
    flex-direction: column;
    gap: 1rem;
}

.edit-area {
    cursor: pointer;
    color: var(--primary-400, #678277);
    font-family: Inter;
    font-size: 0.9375rem;
    font-style: normal;
    font-weight: 600;
    line-height: 1.125rem;
    /* 120% */
    text-decoration-line: underline;
    white-space: nowrap;
}

.checkbox-field {
    display: flex;
    align-items: center;
    gap: 0.5rem;
}

.frequency-group {
    display: flex;
    align-items: center;
    gap: var(--Spacing-space-2, 8px);
    align-self: stretch;
}

.frequency-group select {
    padding: 0.5rem;
    border: 1px solid #ccc;
    border-radius: 0.5rem;
}

.submit-btn {
    margin-top: 1rem;
    padding: 0.75rem;
    background-color: #fcd34d;
    border: none;
    border-radius: 0.5rem;
    font-weight: 600;
    cursor: pointer;
}

.field-label {
    font-weight: 600;
    margin-bottom: 0.25rem;
    display: block;
}
</style>
